import React, { useState, useEffect, useRef } from "react";

const TOKENS = {
  ink: "#1B2430",
  paper: "#F6F4EE",
  paperDim: "#EDEAE0",
  navy: "#16324F",
  navyDeep: "#0E2036",
  gold: "#C69A3E",
  line: "#D8D2C0",
  white: "#FFFFFF",
};

const testimonials = [
  {
    quote:
      "Booking through Trip Caliber felt like checking in with someone who already knew my route. The whole thing took four minutes, gate to confirmation.",
    name: "Alka Rawat",
    from: "DEL → BOM",
    rating: 4,
  },
  {
    quote:
      "I got reminder emails before my flight with baggage limits and check-in windows. Small thing, saved me at the counter.",
    name: "Mohan Sharma",
    from: "BLR → SIN",
    rating: 5,
  },
  {
    quote:
      "Every Delhi–Mumbai run, same result: best fare on the board and a support line that actually picks up.",
    name: "Ritesh Kumar",
    from: "DEL → BOM",
    rating: 5,
  },
];

const services = [
  { code: "FLT", label: "Flight" },
  { code: "HTL", label: "Hotel" },
  { code: "BUS", label: "Bus" },
  { code: "CAB", label: "Cab" },
  { code: "CRZ", label: "Cruise" },
];

const reasons = [
  {
    tag: "01",
    title: "Quick Booking",
    body: "Search, compare, confirm — three steps, no detours. We keep the path from fare to ticket as short as the flight itself.",
  },
  {
    tag: "02",
    title: "Lowest Price",
    body: "Direct lines into major carriers means we see fares as they move, and pass the gap straight to you.",
  },
  {
    tag: "03",
    title: "Exciting Offers",
    body: "Seasonal routes, last-minute seats, partner fares — surfaced when they're live, not buried in a newsletter.",
  },
  {
    tag: "04",
    title: "Extensive Options",
    body: "Budget carriers to premium cabins, red-eyes to red-carpet — the full board, sorted your way.",
  },
  {
    tag: "05",
    title: "User-Friendly Platform",
    body: "Built for the person booking at 11pm on a phone with 12% battery. No clutter, no guessing.",
  },
  {
    tag: "06",
    title: "24/7 Support",
    body: "A real desk, always staffed. Changes, cancellations, or just a question — one call away, any hour.",
  },
];

function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 0.7s cubic-bezier(.22,.68,0,1) ${delay}ms, transform 0.7s cubic-bezier(.22,.68,0,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function Stars({ n }) {
  return (
    <span aria-label={`${n} out of 5 stars`} style={{ letterSpacing: 2, color: TOKENS.gold, fontSize: 13 }}>
      {"★".repeat(n)}
      <span style={{ color: TOKENS.line }}>{"★".repeat(5 - n)}</span>
    </span>
  );
}

export default function AboutUs() {
  const [active, setActive] = useState(0);
  const [heroIn, setHeroIn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroIn(true), 80);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % testimonials.length), 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ background: TOKENS.paper, color: TOKENS.ink, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        .tc-mono { font-family: 'JetBrains Mono', monospace; }
        .tc-display { font-family: 'Fraunces', serif; }
        @media (prefers-reduced-motion: reduce) {
          .tc-anim, .tc-anim * { animation: none !important; transition: none !important; }
        }
        .tc-focusable:focus-visible {
          outline: 2px solid ${TOKENS.gold};
          outline-offset: 3px;
        }
      `}</style>

      {/* ============ HERO — boarding pass ============ */}
      <section
        style={{
          padding: "clamp(48px,8vw,96px) 20px 64px",
          background: `linear-gradient(180deg, ${TOKENS.navyDeep} 0%, ${TOKENS.navy} 100%)`,
          color: TOKENS.paper,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "repeating-linear-gradient(115deg, rgba(255,255,255,0.035) 0px, rgba(255,255,255,0.035) 1px, transparent 1px, transparent 64px)",
          }}
        />
        <div style={{ maxWidth: 1040, margin: "0 auto", position: "relative" }}>
          <div
            className="tc-mono"
            style={{
              fontSize: 12,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: TOKENS.gold,
              opacity: heroIn ? 1 : 0,
              transform: heroIn ? "translateY(0)" : "translateY(10px)",
              transition: "opacity 0.6s ease, transform 0.6s ease",
            }}
          >
            Boarding Pass · Since Origin
          </div>

          <h1
            className="tc-display"
            style={{
              fontSize: "clamp(38px,6.4vw,74px)",
              fontWeight: 500,
              lineHeight: 1.02,
              margin: "18px 0 0",
              maxWidth: 760,
              opacity: heroIn ? 1 : 0,
              transform: heroIn ? "translateY(0)" : "translateY(16px)",
              transition: "opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s",
            }}
          >
            Every trip starts
            <br />
            with a clean itinerary.
          </h1>

          <p
            style={{
              maxWidth: 520,
              marginTop: 22,
              fontSize: 16,
              lineHeight: 1.65,
              color: "rgba(246,244,238,0.72)",
              opacity: heroIn ? 1 : 0,
              transition: "opacity 0.8s ease 0.25s",
            }}
          >
            Trip Caliber books flights, stays, and everything between, for
            travellers who'd rather spend five minutes planning than fifty.
          </p>

          {/* boarding pass strip */}
          <div
            className="tc-anim"
            style={{
              marginTop: 44,
              background: TOKENS.paper,
              color: TOKENS.ink,
              borderRadius: 14,
              display: "flex",
              flexWrap: "wrap",
              overflow: "hidden",
              boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
              opacity: heroIn ? 1 : 0,
              transform: heroIn ? "translateY(0)" : "translateY(24px)",
              transition: "opacity 0.8s ease 0.35s, transform 0.8s ease 0.35s",
            }}
          >
            <div style={{ flex: "1 1 260px", padding: "22px 26px", minWidth: 220 }}>
              <div className="tc-mono" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#8A93A0", textTransform: "uppercase" }}>
                Passenger
              </div>
              <div className="tc-display" style={{ fontSize: 22, marginTop: 4 }}>Trip Caliber</div>
              <div className="tc-mono" style={{ fontSize: 11, color: "#8A93A0", marginTop: 10 }}>EST. TRAVEL PARTNER</div>
            </div>
            <div
              style={{
                flex: "1 1 200px",
                padding: "22px 26px",
                borderLeft: `1px dashed ${TOKENS.line}`,
                display: "flex",
                alignItems: "center",
                gap: 14,
                minWidth: 200,
              }}
            >
              <div className="tc-mono" style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 600 }}>PLAN</div>
                <div style={{ fontSize: 10, color: "#8A93A0" }}>Origin</div>
              </div>
              <div style={{ flex: 1, height: 1, background: `repeating-linear-gradient(90deg, ${TOKENS.line} 0 6px, transparent 6px 12px)`, position: "relative" }}>
                <span style={{ position: "absolute", right: -2, top: -6, fontSize: 12 }}>✈</span>
              </div>
              <div className="tc-mono" style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 600 }}>BOOK</div>
                <div style={{ fontSize: 10, color: "#8A93A0" }}>Destination</div>
              </div>
            </div>
            <div
              style={{
                flex: "0 1 180px",
                padding: "22px 26px",
                borderLeft: `1px dashed ${TOKENS.line}`,
                minWidth: 160,
              }}
            >
              <div className="tc-mono" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#8A93A0", textTransform: "uppercase" }}>
                Class
              </div>
              <div className="tc-display" style={{ fontSize: 22, marginTop: 4 }}>Any Budget</div>
              <div className="tc-mono" style={{ fontSize: 11, color: "#8A93A0", marginTop: 10 }}>SEAT: OPEN</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MISSION ============ */}
      <section style={{ padding: "clamp(56px,8vw,96px) 20px" }}>
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr)", gap: 40 }}>
            <Reveal>
              <div
                className="tc-mono"
                style={{ fontSize: 12, letterSpacing: "0.16em", color: TOKENS.navy, textTransform: "uppercase" }}
              >
                Gate 01 — Who We Are
              </div>
              <h2
                className="tc-display"
                style={{ fontSize: "clamp(28px,4vw,44px)", fontWeight: 500, marginTop: 12, maxWidth: 680, lineHeight: 1.15 }}
              >
                Welcome to Trip Caliber — the travel desk that reads the fare
                board so you don't have to.
              </h2>
            </Reveal>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px,1fr))", gap: 40, marginTop: 44, alignItems: "start" }}>
            <Reveal delay={100}>
              <div>
                <h3 className="tc-display" style={{ fontSize: 22, fontWeight: 600, marginBottom: 14 }}>
                  Built around the traveller, not the ticket
                </h3>
                <p style={{ fontSize: 15.5, lineHeight: 1.75, color: "#3D4652" }}>
                  We started with one frustration: booking travel took longer
                  than the flight itself. So we cut it down — real-time fares,
                  a search that actually understands dates and cities, and a
                  support desk that answers before you finish the sentence.
                </p>
                <ul style={{ listStyle: "none", padding: 0, marginTop: 24, display: "grid", gap: 16 }}>
                  {[
                    ["Customer-Centric Approach", "We prioritise your needs, for a personalised, seamless booking experience."],
                    ["Innovation", "Cutting-edge technology in service of a simpler user experience."],
                    ["Sustainability", "Advocating for responsible travel practices that protect the planet."],
                  ].map(([title, body]) => (
                    <li key={title} style={{ display: "flex", gap: 14, paddingBottom: 16, borderBottom: `1px solid ${TOKENS.line}` }}>
                      <span aria-hidden style={{ color: TOKENS.gold, fontSize: 18, lineHeight: 1.4 }}>✦</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{title}</div>
                        <div style={{ fontSize: 14, color: "#5B6470", marginTop: 3, lineHeight: 1.55 }}>{body}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div
                style={{
                  borderRadius: 16,
                  overflow: "hidden",
                  border: `1px solid ${TOKENS.line}`,
                  background: TOKENS.white,
                  boxShadow: "0 14px 40px rgba(22,50,79,0.10)",
                }}
              >
                <div
                  style={{
                    aspectRatio: "4/3",
                    background: `linear-gradient(135deg, ${TOKENS.navy}, ${TOKENS.navyDeep})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage:
                        "radial-gradient(circle at 20% 20%, rgba(198,154,62,0.25), transparent 45%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.08), transparent 40%)",
                    }}
                  />
                  <span style={{ fontSize: 64, position: "relative" }}>🧭</span>
                </div>
                <div style={{ padding: "18px 20px" }}>
                  <div className="tc-mono" style={{ fontSize: 11, color: "#8A93A0", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                    Mission
                  </div>
                  <p style={{ fontSize: 14, lineHeight: 1.6, color: "#3D4652", marginTop: 6 }}>
                    Make the booking step disappear, so the only thing left to
                    think about is the trip.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ SERVICES — departure board ============ */}
      <section style={{ padding: "56px 20px", background: TOKENS.paperDim, borderTop: `1px solid ${TOKENS.line}`, borderBottom: `1px solid ${TOKENS.line}` }}>
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>
          <Reveal>
            <div className="tc-mono" style={{ fontSize: 12, letterSpacing: "0.16em", color: TOKENS.navy, textTransform: "uppercase" }}>
              Departure Board
            </div>
            <h2 className="tc-display" style={{ fontSize: "clamp(24px,3.2vw,34px)", fontWeight: 500, marginTop: 10 }}>
              What we book
            </h2>
          </Reveal>

          <div
            className="tc-mono"
            style={{
              marginTop: 28,
              background: TOKENS.navyDeep,
              borderRadius: 10,
              overflow: "hidden",
              color: TOKENS.paper,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "70px 1fr 90px",
                padding: "10px 18px",
                fontSize: 11,
                color: "#8A93A0",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <span>Code</span>
              <span>Service</span>
              <span>Status</span>
            </div>
            {services.map((s, i) => (
              <Reveal key={s.code} delay={i * 70}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "70px 1fr 90px",
                    padding: "13px 18px",
                    fontSize: 14,
                    alignItems: "center",
                    borderBottom: i === services.length - 1 ? "none" : "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <span style={{ color: TOKENS.gold, fontWeight: 600 }}>{s.code}</span>
                  <span style={{ fontFamily: "'Inter', sans-serif" }}>{s.label}</span>
                  <span style={{ color: "#7CE0A8" }}>● OPEN</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHY CHOOSE US ============ */}
      <section style={{ padding: "clamp(56px,8vw,96px) 20px" }}>
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>
          <Reveal>
            <div className="tc-mono" style={{ fontSize: 12, letterSpacing: "0.16em", color: TOKENS.navy, textTransform: "uppercase" }}>
              Gate 02 — Why Fly With Us
            </div>
            <h2 className="tc-display" style={{ fontSize: "clamp(26px,3.6vw,38px)", fontWeight: 500, marginTop: 10, maxWidth: 600 }}>
              Six reasons travellers keep coming back
            </h2>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px,1fr))", gap: 1, marginTop: 40, background: TOKENS.line, border: `1px solid ${TOKENS.line}`, borderRadius: 14, overflow: "hidden" }}>
            {reasons.map((r, i) => (
              <Reveal key={r.tag} delay={i * 60}>
                <div style={{ background: TOKENS.white, padding: "28px 26px", height: "100%" }}>
                  <div className="tc-mono" style={{ fontSize: 12, color: TOKENS.gold, fontWeight: 600 }}>{r.tag}</div>
                  <h3 className="tc-display" style={{ fontSize: 19, fontWeight: 600, marginTop: 10 }}>{r.title}</h3>
                  <p style={{ fontSize: 14, lineHeight: 1.65, color: "#5B6470", marginTop: 10 }}>{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section style={{ padding: "clamp(56px,8vw,96px) 20px", background: TOKENS.navyDeep, color: TOKENS.paper }}>
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <Reveal>
            <div className="tc-mono" style={{ fontSize: 12, letterSpacing: "0.16em", color: TOKENS.gold, textTransform: "uppercase" }}>
              Arrivals
            </div>
            <h2 className="tc-display" style={{ fontSize: "clamp(26px,3.6vw,36px)", fontWeight: 500, marginTop: 10 }}>
              What our customers say
            </h2>
          </Reveal>

          <div style={{ marginTop: 44, minHeight: 220, position: "relative" }}>
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                aria-hidden={active !== i}
                style={{
                  position: i === active ? "relative" : "absolute",
                  inset: 0,
                  opacity: active === i ? 1 : 0,
                  transition: "opacity 0.6s ease",
                  pointerEvents: active === i ? "auto" : "none",
                }}
              >
                <p className="tc-display" style={{ fontSize: "clamp(18px,2.4vw,23px)", lineHeight: 1.5, fontWeight: 400, color: TOKENS.paper }}>
                  "{t.quote}"
                </p>
                <div style={{ marginTop: 22, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                  <Stars n={t.rating} />
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{t.name}</div>
                  <div className="tc-mono" style={{ fontSize: 12, color: "#8A93A0" }}>{t.from}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 36 }}>
            {testimonials.map((t, i) => (
              <button
                key={t.name}
                className="tc-focusable"
                onClick={() => setActive(i)}
                aria-label={`Show testimonial from ${t.name}`}
                aria-current={active === i}
                style={{
                  width: active === i ? 22 : 8,
                  height: 8,
                  borderRadius: 4,
                  border: "none",
                  background: active === i ? TOKENS.gold : "rgba(255,255,255,0.25)",
                  cursor: "pointer",
                  transition: "width 0.3s ease, background 0.3s ease",
                  padding: 0,
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ============ CLOSING CTA STRIP ============ */}
      <section style={{ padding: "48px 20px", background: TOKENS.paper, borderTop: `1px dashed ${TOKENS.line}` }}>
        <div
          style={{
            maxWidth: 1040,
            margin: "0 auto",
            display: "flex",
            flexWrap: "wrap",
            gap: 20,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div className="tc-mono" style={{ fontSize: 11, letterSpacing: "0.14em", color: "#8A93A0", textTransform: "uppercase" }}>
              Next Departure
            </div>
            <div className="tc-display" style={{ fontSize: 24, marginTop: 4 }}>Ready when you are.</div>
          </div>
          <a
            href="#book"
            className="tc-focusable"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "13px 26px",
              background: TOKENS.navy,
              color: TOKENS.paper,
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 14,
              textDecoration: "none",
            }}
          >
            Start a Search <span aria-hidden>→</span>
          </a>
        </div>
      </section>
    </div>
  );
}